import { useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useSite, tx } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { Reveal, SectionLabel } from "./Reveal";

const LOCATION_BASE = 744;

export default function EsgSimulator() {
  const { content, lang } = useSite();
  const t0 = ui[lang].esg;
  const dz = content.sezioni?.dati || {};
  const t = { ...t0, heading: tx(dz.heading, lang) || t0.heading, sub: tx(dz.sub, lang) || t0.sub };
  const [ratio, setRatio] = useState(68);

  const market = Math.round(LOCATION_BASE * (1 - ratio / 100));
  const impact = LOCATION_BASE - market;

  const months = lang === "it"
    ? ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"]
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const chartData = useMemo(
    () =>
      months.map((m, i) => ({
        m,
        match: Math.min(100, Math.max(5, Math.round(ratio + 9 * Math.sin(i / 1.6)))),
      })),
    [ratio, lang]
  );

  return (
    <section id="reporting" data-testid="esg-dashboard-section" className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="max-w-2xl">
          <SectionLabel>{ui[lang].labels.reporting}</SectionLabel>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-ice">{t.heading}</h2>
          <p className="mt-4 text-base text-muted-foreground">{t.sub}</p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 bg-surface border border-ice/8 rounded-2xl p-6 sm:p-10">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-center">
              <div className="flex flex-col items-center">
                <div data-testid="esg-matching-gauge" className="relative w-[240px]">
                  <svg viewBox="0 0 200 120" className="w-full">
                    <defs>
                      <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3A5BD9" />
                        <stop offset="100%" stopColor="#57B7E8" />
                      </linearGradient>
                    </defs>
                    <path d="M14 112 A 90 90 0 0 1 186 112" fill="none" stroke="#DCE4F0" strokeWidth="14" strokeLinecap="round" />
                    <path
                      d="M14 112 A 90 90 0 0 1 186 112"
                      fill="none"
                      stroke="url(#gaugeGrad)"
                      strokeWidth="14"
                      strokeLinecap="round"
                      pathLength={100}
                      strokeDasharray={`${ratio} 100`}
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                    <span className="font-display text-4xl font-extrabold text-ice">{ratio}%</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{t.gauge}</span>
                  </div>
                </div>
                <div className="w-full max-w-sm mt-8">
                  <label className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-3">{t.slider}</label>
                  <input
                    data-testid="esg-slider-renewable-ratio"
                    type="range"
                    min="0"
                    max="100"
                    value={ratio}
                    onChange={(e) => setRatio(Number(e.target.value))}
                    className="slider-royal w-full"
                  />
                </div>
              </div>

              <div>
                <div data-testid="esg-emissions-calculator" className="grid grid-cols-3 gap-4">
                  <div className="bg-onyx/60 border border-ice/8 rounded-xl p-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.location}</p>
                    <p className="font-display text-xl sm:text-2xl font-bold text-ice mt-2">{LOCATION_BASE}</p>
                    <p className="text-xs text-muted-foreground">tCO₂e</p>
                  </div>
                  <div className="bg-onyx/60 border border-royal/40 rounded-xl p-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-azure">{t.market}</p>
                    <p className="font-display text-xl sm:text-2xl font-bold text-azure mt-2">{market}</p>
                    <p className="text-xs text-muted-foreground">tCO₂e</p>
                  </div>
                  <div className="bg-onyx/60 border border-ice/8 rounded-xl p-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.impact}</p>
                    <p className="font-display text-xl sm:text-2xl font-bold text-mint mt-2">-{impact}</p>
                    <p className="text-xs text-muted-foreground">tCO₂e</p>
                  </div>
                </div>
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.monthly}</p>
                <div className="mt-3 h-[200px]">
                  <ResponsiveContainer width="100%" height="100%" debounce={100}>
                    <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#57B7E8" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#57B7E8" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="m" interval={2} tick={{ fill: "#8FA3C0", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis hide domain={[0, 100]} />
                      <Tooltip
                        contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(14,27,51,0.12)", borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ color: "#8FA3C0" }}
                        itemStyle={{ color: "#57B7E8" }}
                      />
                      <Area type="monotone" dataKey="match" stroke="#57B7E8" strokeWidth={2} fill="url(#areaGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-5 text-xs text-muted-foreground italic">{t.demo}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
